// ModernPhoneShell — スマホ (viewport < 640px) 向けの Modern フォンシェル骨格
// (docs/_wip/modern-phone-shell-2026-07-02.md §0 / §3.1 M1-b、consult 2026-07-02)。
//
// 位置づけ: スマホ版 = 第3レイアウトではなく **「Modern のフォンシェル」**。論理
// レイアウトは Modern のまま (スマホ = Modern 固定は不変)、presentation をデスクトップ
// シェル / フォンシェルの 2 ホストに分け、既存パネル部品を「全画面 canvas + 下部バー +
// ボトムシート」に再ホストする (C案)。UI イディオムは ibisPaint / CSP スマホ版の作法
// (キャンバス全画面・親指域の下部バー・ボトムシート)。
//
// 構成:
//   - 上部: 最小ステータスバー (接続状態ドット / 部屋名 / 参加人数)。RoomHeader の
//     ボタン群 (全画面/表示/設定...) は M1 では持たない (= 高さを抑える。M2 で取捨)。
//   - 中央: 全画面 canvas 空スロット (panels.canvas() = canvasSlotRef 付き div。
//     CanvasStage が RoomLayout 直下からこの矩形を useSlotRect で計測して重なる既存
//     機構 §9.7 をそのまま使う。canvas 本体はここに無い)。
//   - 下部バー (親指域): ペン / 消しゴム切替 + シート入口 (ブラシ設定 / 色 / レイヤー /
//     チャット) + オーバーフロー (clear 等の権限系コマンド = ModernCommandCluster 相当
//     + 退室)。ヒットターゲットは 44px (h-11 w-11) 目安。
//   - ボトムシート: panelRegistry の render クロージャを再ホストする (= パネル部品
//     本体は不触、ホスト側 CSS で吸収)。**同時に開くのは 1 枚** (排他)。シート外
//     (backdrop) タップ / 閉じるボタン / 同ボタン再タップで閉じる。バーは backdrop の
//     外なので、開いたまま別シートへ直接切替できる (ibisPaint 流)。
//     チャットシートは ModernSocialDock が flex-1 前提なのでシート側で高さ (dvh) を与える。
//
// 描画能力は増やさない (M1-b 不変条件): ズーム / フィット / タッチジェスチャは並行
// スライス (M1-a / M1-c) の担当で、ここには zoom ロジックを実装しない。protocol /
// server / engine 無影響。desktop 専用機構 (DnD / splitter / レール 4 スロット) は
// 使わない。
//
// safe-area: シェル root に env(safe-area-inset-top/left/right)、下部バーに
// env(safe-area-inset-bottom) の padding (index.html は viewport-fit=cover)。

import type { StrokePen } from '@oekaki/protocol';
import { useCallback, useState } from 'react';
import { useGuardedTap } from '../../hooks/useGuardedTap.js';
import { useTapActivate } from '../../hooks/useTapActivate.js';
import {
  BrushIcon,
  EraserIcon,
  HelpIcon,
  InviteIcon,
  RegionIcon,
  RotateIcon,
} from './ModernIcons.js';
import { formatRotationLabel } from './modernTools.js';
import type { PanelId } from './workspaceLayout.js';

/** ボトムシートの id (= 下部バーの入口と 1:1、排他で 1 枚だけ開く)。 */
export type PhoneSheetId = 'tool' | 'color' | 'layer' | 'chat' | 'more';

/** シート表示名 (= シートヘッダ + 入口ボタンの aria-label 語彙)。 */
export const PHONE_SHEET_LABEL: Readonly<Record<PhoneSheetId, string>> = {
  tool: 'ブラシ設定',
  color: 'カラー',
  layer: 'レイヤー',
  chat: 'チャット',
  more: 'その他',
};

export interface ModernPhoneShellProps {
  /** 上部ステータスバー: 部屋名 (RoomHeader の roomLabel と同じ導出)。 */
  readonly roomLabel: string;
  /** 上部ステータスバー: 参加人数。 */
  readonly memberCount: number;
  /** 上部ステータスバー: 接続状態 (= view.phase 'joined')。false なら再接続中扱い。 */
  readonly connected: boolean;
  /** 現在のペン種 (= ペン/消しゴムボタンのハイライト導出)。 */
  readonly pen: StrokePen;
  /** 現在の描画色 rgb24 (= カラー入口ボタンのスウォッチ表示)。 */
  readonly penColorRgb24: number;
  /**
   * 選択中レイヤー id (= PenConfig.layerId)。レイヤー入口ボタンに「id + 1」の番号
   * バッジを重ねる (レイヤー欄 ModernLayerPanel の番号と同じ表記)。
   */
  readonly layerId: number;
  /** ペン (ブラシ) 選択 (= ModernLayout.handleSelectBrush、スポイト解除込み)。 */
  readonly onSelectBrush: () => void;
  /** 消しゴム選択 (= ModernLayout.handleSelectEraser)。 */
  readonly onSelectEraser: () => void;
  /**
   * 範囲の道具 (範囲の移動・コピー) が選択中か。案 B (作者 2026-10-15): 下部バーは 7 個のまま、
   * 「ブラシ設定」シートの道具切替 `[ペン][消しゴム][範囲]` で選ぶ。選ぶとバーの「ペン」の位置が
   * 範囲のアイコンになる。省略時は道具切替を出さない。
   */
  readonly regionActive?: boolean;
  readonly onSelectRegion?: () => void;
  /** 範囲の「New!」バッジ (バーのブラシ設定ボタンとシート内の [範囲])。範囲を一度でも選ぶと消える。 */
  readonly showRegionNewBadge?: boolean;
  /** 退室 (= useSession().leave)。オーバーフローシート内に置く。 */
  readonly onLeave: () => void;
  /**
   * ルーム設定 (= RoomSettingsModal) を開く。オーバーフローシート内に「ルーム設定」
   * 項目として置く (= desktop の RoomHeader「ルーム設定」と同じ流儀、全員に見せる。
   * 非オーナーが押してもモーダル側がログイン/オーナー認証を促すので無害)。retention
   * チップ (P1) / hint (P2) のタップ先も同じ = 登録して部屋を残す導線がスマホから届く。
   */
  readonly onOpenSettings: () => void;
  /**
   * 使い方マニュアルを開く (2026-09-22 目立たせ改修)。ModernLayout が
   * `helpButtonSeen` store を所有し、押下ハンドラ (= seen 化 + 別タブ遷移) を渡す
   * (= Classic/Modern デスクトップと同じ store key で seen 状態を共有するため、
   * この shell 自身は localStorage を扱わない)。オーバーフローシート内「その他」に
   * 「ルーム設定」の隣で置く。
   */
  readonly onHelpClick: () => void;
  /** 招待ダイアログ (部屋 URL の コピー / 共有 / QR) を開く。「その他」シート内に置く。 */
  readonly onInviteClick: () => void;
  /** 新機能の紹介ダイアログを開く。「その他」シート内に置く (whats-new-2026-10-15.md)。 */
  readonly onWhatsNewClick: () => void;
  /** 未読の印を出すか (「その他」入口ボタンとシート内ボタンの両方に付く)。 */
  readonly whatsNewUnread: boolean;
  /**
   * 表示回転 (canvas-rotation 段取り 3)。2 本指ひねりで回した後に戻す手段として、0 以外の
   * ときだけ上部ステータスバーに角度チップを出す (押すと 0°)。省略時は出さない。
   */
  readonly rotation?: number;
  readonly onRotateReset?: () => void;
  /** 使い方ボタンの「New!」バッジを出すか (= 初回未クリック、ModernLayout 側で判定)。 */
  readonly helpSeen: boolean;
  /**
   * retention 案内チップ (= RoomRetentionChip)。desktop と同じ流儀でノードを差し込む
   * (= owner-only は チップ側で self-gate、非オーナーは null)。上部ステータスバーの
   * 部屋名の近くに描く。渡さなければ何も出さない。
   */
  readonly retentionChip?: React.ReactNode;
  /**
   * ModernLayout の panelRegistry (= PanelId → render クロージャ)。フォンシェルは
   * canvas スロットとシート中身 (toolProperties / colorPanel / colorPalette /
   * layerPanel / socialDock / commandCluster) をここから再ホストする。
   * デスクトップ専用パネル (toolRail / previewPanel) は使わない。
   */
  readonly panels: Readonly<Record<PanelId, () => React.ReactNode>>;
}

/** rgb24 → CSS カラー文字列 (カラー入口ボタンのスウォッチ)。 */
const rgb24ToCss = (rgb24: number): string =>
  `#${(rgb24 & 0xffffff).toString(16).padStart(6, '0')}`;

// ボトムシート内で「画面端いっぱいの水平ドラッグ操作」を物理画面の左右端から離すための
// 水平インセット (2026-07-06)。
//
// 背景 (実機確認): スマホでシート内の full-width スライダー
// (太さ/不透明度/番手 = ModernToolProperties の <input type=range>、カラーのチャンネルバー
// = ColorPanel の `.modern-channel-bar`) を、指が物理画面の左右端に近い状態でドラッグすると、
// Android のシステムジェスチャ (画面端スワイプ = OS の「戻る」) が発火してブラウザバックする。
// これは touch-action / overscroll-behavior / 非 passive preventDefault のいずれでも止められない
// (OS が web にタッチイベントを渡す前に横取りするため)。唯一の実効策は操作トラックを画面端から
// 離すこと。よってシート content に水平インセットを足し、トラック端を端から遠ざける。
//
// 検算 (~360px viewport, safe-area=0): トラック端までのインセット =
//   safe-area-inset-left(0) + シート枠 border(1px) + 本クラス px-6(24px)
//   + パネル section の p-2(8px) = 33px >= 32px (Android ジェスチャゾーン ~24dp を安全に超える)。
// safe-area は加算方向なのでノッチ端末では更に広がる (= 33px は下限)。
//
// desktop の docked パネル (ModernLayout が panelRegistry を直接ホスト) はこのラッパを通らない
// ので不変 = インセットはフォンシェル限定 (デスクトップ Modern の幅を無駄にしない)。
// full-width 水平ドラッグを持たないシート (layer/chat/more) には適用しない。
const SHEET_EDGE_INSET_CLASS = 'px-6' as const;

/** 下部バーのボタン共通スタイル (44px ヒットターゲット、ToolRail の視覚言語を踏襲)。 */
const barButtonClass = (active: boolean): string =>
  `relative flex h-11 w-11 shrink-0 items-center justify-center rounded-md border transition-colors ${
    active
      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black'
      : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white'
  }`;

/**
 * 下部バーの 1 ボタン。起動は pointer ベース (useTapActivate) = ペン/Windows Ink の
 * click 不発対策の共通流儀 (ToolRailButton と同型)。
 */
const PhoneBarButton = ({
  ariaLabel,
  active,
  onActivate,
  opensSheet = false,
  manualId,
  manualView,
  unreadDot = false,
  newBadge = false,
  children,
}: {
  readonly ariaLabel: string;
  readonly active: boolean;
  readonly onActivate: () => void;
  /** シート入口ボタンなら aria-haspopup="dialog" を付ける。 */
  readonly opensSheet?: boolean;
  /**
   * 使い方マニュアル (user-manual-2026-09-19.md §10.2/§10.4) の `data-manual` id。
   * シート入口ボタン (ブラシ設定/カラー/レイヤー/チャット/その他) には**付けない**
   * (§10.4: 開くボタン自体に子項目の id を付けると二重に光る)。単独で完結する項目
   * (例: 消しゴム) にだけ渡す。`manualView` とは排他 (同じ要素に両方付くことは無い)。
   */
  readonly manualId?: string;
  /**
   * 使い方マニュアル §10.4 末尾「バーのアイコンを押すとそのシートの画面へ切り替わる
   * hotspot」の契約。シート入口ボタン (ブラシ設定/カラー/レイヤー/チャット/その他)
   * に、遷移先の `PhoneSheetId` (= `ManualScreenView.id`) を渡す。撮影は
   * `{ view, x, y, w, h }` (id 無し) として拾う。ペン/消しゴムはシートを開かないので
   * 付けない。
   */
  readonly manualView?: PhoneSheetId;
  /** 未読の印 (右上の小さなドット。新機能の紹介が「その他」シートにあることの目印)。 */
  readonly unreadDot?: boolean;
  /** 「New!」ピル (新機能の入口の印)。意味は呼び出し側が ariaLabel に「(新機能)」で添える。 */
  readonly newBadge?: boolean;
  readonly children: React.ReactNode;
}): React.ReactNode => {
  const tap = useTapActivate(onActivate);
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      title={ariaLabel}
      aria-pressed={active}
      {...(opensSheet ? ({ 'aria-haspopup': 'dialog' } as const) : {})}
      {...(manualId !== undefined ? { 'data-manual': manualId } : {})}
      {...(manualView !== undefined ? { 'data-manual-view': manualView } : {})}
      {...tap}
      className={barButtonClass(active)}
    >
      {children}
      {newBadge ? (
        <span
          aria-hidden="true"
          data-testid="phone-region-new-badge-bar"
          className="top-0.5 right-0.5 absolute animate-pulse rounded-full border border-[var(--color-panel)] bg-[var(--color-accent)] px-1 py-px font-bold text-[8px] text-black leading-none shadow"
        >
          New!
        </span>
      ) : null}
      {unreadDot ? (
        <span
          data-testid="unread-dot"
          aria-hidden="true"
          className="pointer-events-none absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[var(--color-accent)] ring-1 ring-black/60"
        />
      ) : null}
    </button>
  );
};

// --- フォンシェル専用の入口アイコン (ModernIcons と同じ 24x24 / currentColor 線画。
//     デスクトップに無い語彙 (レイヤー/チャット/スライダ/⋯) なのでローカル定義に留め、
//     共有アイコン群は汚さない)。 ---

/**
 * アイコン共通の svg 属性 (ModernIcons と同じ 24x24 / currentColor 線画)。装飾目的
 * なので aria-hidden は各 svg にリテラルで付ける (= 意味はボタンの aria-label が担う)。
 */
const iconSvgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  className: 'h-5 w-5',
} as const;

/** ブラシ設定 (スライダ)。 */
const SlidersIcon = (): React.ReactNode => (
  <svg {...iconSvgProps} aria-hidden="true">
    <path d="M4 8h10M18 8h2M4 16h4M12 16h8" />
    <circle cx="15" cy="8" r="2" />
    <circle cx="9" cy="16" r="2" />
  </svg>
);

/** レイヤー (重なる菱形)。 */
const LayersIcon = (): React.ReactNode => (
  <svg {...iconSvgProps} aria-hidden="true">
    <path d="m12 3 9 5-9 5-9-5z" />
    <path d="m3 13 9 5 9-5" />
  </svg>
);

/** チャット (吹き出し)。 */
const ChatBubbleIcon = (): React.ReactNode => (
  <svg {...iconSvgProps} aria-hidden="true">
    <path d="M21 12a8 8 0 0 1-8 8H4l2.5-2.5A8 8 0 1 1 21 12z" />
  </svg>
);

/** オーバーフロー (⋯)。 */
const MoreIcon = (): React.ReactNode => (
  <svg {...iconSvgProps} aria-hidden="true">
    <circle cx="5" cy="12" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
  </svg>
);

/** 「ブラシ設定」シート先頭の道具切替 `[ペン] [消しゴム] [範囲]`。 */
const PhoneToolSwitcher = ({
  pen,
  regionActive,
  onSelectBrush,
  onSelectEraser,
  onSelectRegion,
  showRegionNewBadge,
}: {
  readonly pen: StrokePen;
  readonly regionActive: boolean;
  readonly onSelectBrush: () => void;
  readonly onSelectEraser: () => void;
  readonly onSelectRegion: () => void;
  readonly showRegionNewBadge: boolean;
}): React.ReactNode => (
  <fieldset
    aria-label="道具"
    className="m-0 mb-2 flex gap-1.5 border-0 p-0"
    data-testid="phone-tool-switcher"
  >
    <PhoneToolSegment
      label="ペン"
      pressed={!regionActive && pen !== 'eraser'}
      onActivate={onSelectBrush}
      icon={<BrushIcon className="h-4 w-4" />}
    />
    <PhoneToolSegment
      label="消しゴム"
      pressed={!regionActive && pen === 'eraser'}
      onActivate={onSelectEraser}
      icon={<EraserIcon className="h-4 w-4" />}
    />
    <PhoneToolSegment
      label="範囲"
      pressed={regionActive}
      onActivate={onSelectRegion}
      icon={<RegionIcon className="h-4 w-4" />}
      manualId="draw.region-move"
      newBadge={showRegionNewBadge}
    />
  </fieldset>
);

const PhoneToolSegment = ({
  label,
  pressed,
  onActivate,
  icon,
  manualId,
  newBadge = false,
}: {
  readonly manualId?: string;
  readonly newBadge?: boolean;
  readonly label: string;
  readonly pressed: boolean;
  readonly onActivate: () => void;
  readonly icon: React.ReactNode;
}): React.ReactNode => {
  const tap = useGuardedTap(onActivate);
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={newBadge ? `${label} (新機能)` : undefined}
      data-testid={`phone-tool-${label}`}
      data-manual={manualId}
      {...tap}
      className={`relative flex h-11 flex-1 items-center justify-center gap-1.5 rounded-md border text-[13px] font-bold transition-colors ${
        pressed
          ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black'
          : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white'
      }`}
    >
      {icon}
      {label}
      {newBadge ? (
        <span
          aria-hidden="true"
          data-testid="phone-region-new-badge-sheet"
          className="-top-1.5 -right-1 absolute animate-pulse rounded-full border border-[var(--color-panel)] bg-[var(--color-accent)] px-1 py-px font-bold text-[8px] text-black leading-none shadow"
        >
          New!
        </span>
      ) : null}
    </button>
  );
};

export const ModernPhoneShell = ({
  roomLabel,
  memberCount,
  connected,
  pen,
  penColorRgb24,
  layerId,
  onSelectBrush,
  onSelectEraser,
  regionActive = false,
  onSelectRegion,
  showRegionNewBadge = false,
  onLeave,
  onOpenSettings,
  onHelpClick,
  onInviteClick,
  onWhatsNewClick,
  whatsNewUnread,
  rotation = 0,
  onRotateReset,
  helpSeen,
  retentionChip,
  panels,
}: ModernPhoneShellProps): React.ReactNode => {
  // 開いているシート (排他で 1 枚)。null = 全閉。
  const [openSheet, setOpenSheet] = useState<PhoneSheetId | null>(null);
  const closeSheet = useCallback((): void => setOpenSheet(null), []);
  // 入口ボタン: 同ボタン再タップで閉じる toggle、別シートへは直接切替 (排他)。
  const toggleSheet = useCallback((id: PhoneSheetId): void => {
    setOpenSheet((prev) => (prev === id ? null : id));
  }, []);

  // シート中身: panelRegistry の render クロージャを再ホストする (パネル本体は不触、
  // 収め方 = 高さ/スクロール/幅 cap はホスト側 CSS で吸収する)。
  const sheetBody = (id: PhoneSheetId): React.ReactNode => {
    switch (id) {
      case 'tool':
        // ブラシ一覧 (どのブラシか) → ツールプロパティ (そのブラシをどう調整するか)。
        // スマホには常設パネルの概念が無く、新しいシートを増やすと下部バーが狭い画面を
        // 圧迫するので、新シートは追加せずこの 'tool' シートの中にブラシ一覧を差し込む
        // (modern-brush-panel-2026-08-17.md §7)。並び順はデスクトップ toolColumn と同じ
        // 「どのブラシか → どう調整するか」。縦に長いので内部スクロール。
        // 水平インセット (SHEET_EDGE_INSET_CLASS): full-width スライダーを画面端から離し、
        // Android のエッジスワイプ「戻る」誤発火を防ぐ (px-6、上記コメントの検算参照)。
        return (
          <div
            className={`max-h-[55dvh] overflow-y-auto overscroll-contain ${SHEET_EDGE_INSET_CLASS} py-2`}
          >
            {onSelectRegion !== undefined ? (
              <PhoneToolSwitcher
                pen={pen}
                regionActive={regionActive}
                showRegionNewBadge={showRegionNewBadge}
                onSelectBrush={onSelectBrush}
                onSelectEraser={onSelectEraser}
                // 範囲を選んだらシートを閉じる (次の操作はキャンバスのドラッグなので、
                // シートの backdrop が邪魔にならないように)。
                onSelectRegion={() => {
                  onSelectRegion();
                  closeSheet();
                }}
              />
            ) : null}
            <div className="mb-2">{panels.brushPanel()}</div>
            {panels.toolProperties()}
          </div>
        );
      case 'color':
        // カラーホイール + 16色/最近色。ホイールが横に伸びすぎないよう幅 cap + 中央寄せ。
        // 水平インセット (SHEET_EDGE_INSET_CLASS): チャンネルバー (.modern-channel-bar) の
        // full-width 水平ドラッグを画面端から離す (太さスライダーと同じ OS ジェスチャ対策)。
        // HueRing は中央寄せの固定径で元々端に達しないが、ラッパ単位で一律に離しておく。
        return (
          <div
            className={`max-h-[65dvh] overflow-y-auto overscroll-contain ${SHEET_EDGE_INSET_CLASS} py-2`}
          >
            <div className="mx-auto flex w-full max-w-[320px] flex-col gap-2">
              {panels.colorPanel()}
              {panels.colorPalette()}
            </div>
          </div>
        );
      case 'layer':
        return (
          <div
            className={`max-h-[55dvh] overflow-y-auto overscroll-contain ${SHEET_EDGE_INSET_CLASS} py-2`}
          >
            {panels.layerPanel()}
          </div>
        );
      case 'chat':
        // ModernSocialDock は flex-1 前提 (メンバー shrink-0 + チャット flex-1) なので、
        // シート側で確定高さ (dvh) を与えて flex 縦コンテナに入れる。ソフトキーボード
        // (visualViewport) 統合は M2 送り (設計メモ §1 ギャップ3)。
        return <div className="flex h-[55dvh] min-h-0 flex-col p-2">{panels.socialDock()}</div>;
      case 'more':
        // 権限系コマンド (全消し/キャプチャ/画像貼付/名前ラベル = ModernCommandCluster
        // 相当) はオーバーフローへ隔離 (誤タップ回避)。ルーム設定 (= 登録して部屋を残す
        // 導線) と退室もここ。ルーム設定と退室は行を分け、退室は末尾に離して置く
        // (= 破壊的操作の誤タップ回避。desktop RoomHeader は「ルーム設定 / 退室」を隣接
        // させるが、フォンは指が太いので縦に分離する)。
        return (
          <div className="flex max-h-[55dvh] flex-col gap-3 overflow-y-auto overscroll-contain p-3">
            <div className="flex items-start gap-4">{panels.commandCluster()}</div>
            {/* ルーム設定: 全員に見せる (desktop の RoomHeader「ルーム設定」と同じ流儀)。
                非オーナーが押してもモーダル側がログイン/認証を促すので無害。 */}
            <button
              type="button"
              onClick={onOpenSettings}
              data-manual="room.settings"
              className="flex h-11 shrink-0 items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-4 text-[13px] font-bold text-white"
            >
              ルーム設定
            </button>
            {/* 招待 (room-invite-2026-10-01.md): 部屋 URL を友達に渡す導線。ルーム設定の
                直下 (退室とは離す)。ダイアログは ModernLayout が所有。 */}
            <button
              type="button"
              onClick={onInviteClick}
              className="flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-4 text-[13px] font-bold text-white"
            >
              <InviteIcon className="h-4 w-4" />
              友達を招待
            </button>
            {/* 新機能の紹介 (whats-new-2026-10-15.md): 自動では開かない。未読のときだけ
                控えめなドット。ダイアログは ModernLayout が所有。 */}
            <button
              type="button"
              onClick={onWhatsNewClick}
              className="flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-4 text-[13px] font-bold text-white"
            >
              新機能の紹介
              {whatsNewUnread ? (
                <span
                  data-testid="unread-dot"
                  className="inline-block h-2 w-2 rounded-full bg-[var(--color-accent)] ring-1 ring-white/70"
                >
                  <span className="sr-only">未読あり</span>
                </span>
              ) : null}
            </button>
            {/* 使い方マニュアル (作者決定 2026-09-22、目立たせ改修): ルーム設定の隣に
                置く。別タブで www の `/help/?env=phone` を開く。マニュアル項目としては
                扱わない (= data-manual を付けない、manualRegistry.ts の登録対象外)。
                他ボタンと違うティール系アクセント (RoomHeader scope:'help' と同色) +
                丸囲み HelpIcon + 初回のみ「New!」バッジ。 */}
            <button
              type="button"
              onClick={onHelpClick}
              className="flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-md border border-slate-600 bg-gradient-to-b from-slate-50 via-slate-200 to-slate-400 px-4 text-[13px] font-bold text-slate-900 shadow-[inset_0_1px_0_#fff,inset_0_-1px_0_rgba(0,0,0,0.15),0_3px_0_#1e293b,0_5px_8px_rgba(0,0,0,0.4)] hover:from-white hover:to-slate-300 active:translate-y-[2px] active:shadow-[inset_0_1px_0_#fff,0_1px_0_#1e293b]"
            >
              <HelpIcon className="h-4 w-4" />
              使い方
              {!helpSeen ? (
                <span className="ml-0.5 inline-block animate-pulse rounded-full bg-[var(--color-accent)] px-1.5 py-0.5 align-middle text-[9px] font-bold text-black leading-none shadow ring-1 ring-white/70">
                  New!
                </span>
              ) : null}
            </button>
            {/* 退室は破壊的なので末尾に離し、上に区切り線を入れて誤タップを防ぐ。 */}
            <button
              type="button"
              onClick={onLeave}
              data-manual="start.leave"
              className="mt-1 flex h-11 shrink-0 items-center justify-center rounded-md border border-[var(--color-border)] border-t border-t-[var(--color-border)] bg-[var(--color-bg-inset)] px-4 text-[13px] font-bold text-[var(--color-text-muted)]"
            >
              退室
            </button>
          </div>
        );
    }
  };

  const rotationLabel = formatRotationLabel(rotation);

  return (
    <div
      data-testid="modern-phone-shell"
      // 使い方マニュアル (user-manual-2026-09-19.md §10.4) の `layout.phone` 項目
      // (「スマホ用の画面」) には単一のボタンが無いので、シェル全体をその印にする。
      data-manual="layout.phone"
      // safe-area (ノッチ/パンチホール/ラウンド角) は Tailwind arbitrary value で取る
      // (viewport-fit=cover 前提)。下端は下部バー側で取る。
      className="relative flex h-full flex-col bg-[var(--color-bg)] pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)]"
    >
      {/* 上部: 最小ステータスバー (接続ドット / 部屋名 / 参加人数)。高さを抑える
          (py-1)。RoomHeader のボタン群は M1 では出さない (取捨は M2)。 */}
      <header className="flex shrink-0 items-center gap-2 border-[var(--color-border)] border-b bg-[var(--color-panel)] px-3 py-1">
        <span
          data-testid="phone-connection-dot"
          role="status"
          aria-label={connected ? '接続中' : '再接続中'}
          title={connected ? '接続中' : '再接続中'}
          className={`h-2 w-2 shrink-0 rounded-full ${connected ? 'bg-emerald-400' : 'bg-amber-400'}`}
        />
        <span
          className="min-w-0 flex-1 select-none truncate text-[12px] font-bold text-white"
          title={roomLabel}
        >
          {roomLabel}
        </span>
        <span className="shrink-0 select-none text-[11px] text-[var(--color-text-muted)]">
          参加者 {memberCount}
        </span>
        {/* 表示回転の戻し手段 (下部バーは満杯)。回転中だけ出す。部屋名が flex-1 truncate
            なので出入りで他がガタつかない。 */}
        {rotationLabel !== null && onRotateReset !== undefined ? (
          <button
            type="button"
            onClick={onRotateReset}
            aria-label={`回転 ${rotationLabel}、0° に戻す`}
            title="回転をリセット"
            data-testid="rotation-reset-topbar"
            className="flex h-7 shrink-0 items-center gap-1 rounded-full border border-[var(--color-accent)] px-2 font-mono text-[11px] font-bold text-[var(--color-accent)] active:opacity-70"
          >
            <RotateIcon className="h-3.5 w-3.5" />
            {rotationLabel}
          </button>
        ) : null}
        {/* 招待 (room-invite-2026-10-01.md、2026-10-01 作者 fb「スマホでも目立たせたい」)。
            下部バーは 7 ボタンで満杯なので、上部ステータスバーの参加者数の横に置く。
            desktop の RoomHeader scope:'invite' と同じ緑 (--color-success) = 黄
            (selected) と混同しない。部屋名は flex-1 truncate なので、こちらは縮めない。
            「その他」シート内の「友達を招待」も残す (どちらからでも開ける)。 */}
        <button
          type="button"
          onClick={onInviteClick}
          data-testid="invite-topbar"
          className="flex h-7 shrink-0 items-center gap-1 rounded-full border border-emerald-700 bg-[var(--color-success)] px-2.5 text-[11px] font-bold text-slate-900 shadow-[0_1px_0_#065f46] active:translate-y-[1px] active:shadow-none"
        >
          <InviteIcon className="h-3.5 w-3.5" />
          友達を招待
        </button>
      </header>

      {/*
        retention 案内チップ (P1) / 初回 hint (P2) の帯。ステータスバー (py-1 の最小高)
        は狭く、P2 hint (「登録すると部屋が長く残せます →」) を横に並べると溢れるので、
        部屋名の直下の細い全幅帯に落とす (設計メモ「モバイル到達性」)。
        - empty:hidden: RoomRetentionChip は非オーナー / 取得失敗時に null を返す (= DOM
          子が無い → :empty)。その場合この帯ごと消して border/padding を残さない。
        - flex-wrap + [&>div]:flex-wrap: 帯自身と RoomRetentionChip 内側の flex を折返し
          可にして、チップ + hint が横幅を超えても 2 行に畳む (= 横スクロール/はみ出しを
          出さない。RoomRetentionChip の DOM は不触、phone 側の入れ物で吸収)。
      */}
      <div className="flex shrink-0 flex-wrap items-center gap-1 border-[var(--color-border)] border-b bg-[var(--color-panel)] px-3 py-1 empty:hidden [&>div]:flex-wrap">
        {retentionChip}
      </div>

      {/* 中央: 全画面 canvas 空スロット + シート overlay の anchor。canvas 本体は
          CanvasStage (RoomLayout 直下常設) がこのスロット矩形に重なる (§9.7 不変)。 */}
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        {panels.canvas()}

        {/* backdrop: シート外タップで閉じる。下部バーは外側 (= 兄弟) なので、開いた
            まま別シートへ直接切替できる。canvas への誤描画もこれが遮る。 */}
        {openSheet !== null && (
          <button
            type="button"
            aria-label="シートを閉じる"
            data-testid="phone-sheet-backdrop"
            onClick={closeSheet}
            className="absolute inset-0 z-10 cursor-default bg-black/30"
          />
        )}

        {/* ボトムシート (排他 1 枚)。下部バーの直上に出る。 */}
        {openSheet !== null && (
          <section
            role="dialog"
            aria-label={PHONE_SHEET_LABEL[openSheet]}
            data-testid={`phone-sheet-${openSheet}`}
            className="absolute inset-x-0 bottom-0 z-20 flex flex-col rounded-t-xl border border-[var(--color-border)] border-b-0 bg-[var(--color-panel)] shadow-2xl"
          >
            <div className="flex shrink-0 items-center justify-between border-[var(--color-border)] border-b px-3 py-1">
              <span className="select-none text-[12px] font-bold text-white">
                {PHONE_SHEET_LABEL[openSheet]}
              </span>
              <button
                type="button"
                aria-label="閉じる"
                onClick={closeSheet}
                className="flex h-11 w-11 items-center justify-center rounded-md text-[16px] text-[var(--color-text-muted)]"
              >
                ×
              </button>
            </div>
            <div className="min-h-0">{sheetBody(openSheet)}</div>
          </section>
        )}
      </div>

      {/* 下部バー (親指域): ペン/消しゴム + シート入口 + オーバーフロー。44px 目安。 */}
      <nav
        aria-label="クイックツール"
        className="flex shrink-0 items-center justify-around gap-1 border-[var(--color-border)] border-t bg-[var(--color-panel)] px-1 pt-1 pb-[calc(env(safe-area-inset-bottom)_+_4px)]"
      >
        {/* 範囲の道具の間は、ペンの位置が範囲のアイコンになる (押すとペンへ戻る)。 */}
        <PhoneBarButton
          ariaLabel={regionActive ? '範囲' : 'ペン'}
          active={regionActive || pen !== 'eraser'}
          onActivate={onSelectBrush}
        >
          {regionActive ? <RegionIcon /> : <BrushIcon />}
        </PhoneBarButton>
        <PhoneBarButton
          ariaLabel="消しゴム"
          active={pen === 'eraser' && !regionActive}
          onActivate={onSelectEraser}
          manualId="draw.eraser"
        >
          <EraserIcon />
        </PhoneBarButton>
        <PhoneBarButton
          ariaLabel={
            showRegionNewBadge && onSelectRegion !== undefined
              ? `${PHONE_SHEET_LABEL.tool} (新機能)`
              : PHONE_SHEET_LABEL.tool
          }
          newBadge={showRegionNewBadge && onSelectRegion !== undefined}
          active={openSheet === 'tool'}
          onActivate={() => toggleSheet('tool')}
          opensSheet
          manualView="tool"
        >
          <SlidersIcon />
        </PhoneBarButton>
        <PhoneBarButton
          ariaLabel={PHONE_SHEET_LABEL.color}
          active={openSheet === 'color'}
          onActivate={() => toggleSheet('color')}
          opensSheet
          manualView="color"
        >
          {/* 現在色スウォッチ = そのまま入口アイコン (ibisPaint 流)。 */}
          <span
            data-testid="phone-color-swatch"
            aria-hidden="true"
            className="h-6 w-6 rounded-full border border-white/50"
            style={{ backgroundColor: rgb24ToCss(penColorRgb24) }}
          />
        </PhoneBarButton>
        <PhoneBarButton
          ariaLabel={`${PHONE_SHEET_LABEL.layer} (${layerId + 1} を選択中)`}
          active={openSheet === 'layer'}
          onActivate={() => toggleSheet('layer')}
          opensSheet
          manualView="layer"
        >
          <span className="relative inline-flex">
            <LayersIcon />
            {/* 選択中レイヤー番号。key で選び直しごとに再マウント = 一瞬ふくらむ。 */}
            <span
              key={layerId}
              data-testid="phone-layer-badge"
              aria-hidden="true"
              className="phone-layer-badge pointer-events-none absolute -bottom-1.5 -right-2 flex h-4 min-w-4 items-center justify-center rounded-full border border-white bg-black px-1 text-[11px] font-bold leading-none text-white"
            >
              {layerId + 1}
            </span>
          </span>
        </PhoneBarButton>
        <PhoneBarButton
          ariaLabel={PHONE_SHEET_LABEL.chat}
          active={openSheet === 'chat'}
          onActivate={() => toggleSheet('chat')}
          opensSheet
          manualView="chat"
        >
          <ChatBubbleIcon />
        </PhoneBarButton>
        <PhoneBarButton
          ariaLabel={PHONE_SHEET_LABEL.more}
          active={openSheet === 'more'}
          onActivate={() => toggleSheet('more')}
          opensSheet
          manualView="more"
          unreadDot={whatsNewUnread}
        >
          <MoreIcon />
        </PhoneBarButton>
      </nav>
    </div>
  );
};
